import React, {useEffect, useMemo, useState} from "react";
import {targets, useReferentialContext} from "../../Context";
import dayjs from "dayjs";
import WindowContainer from "../../components/layout/WindowContainer";
import FilterSet from "../../components/selectors/Filterset";
import {capitalize, CircularProgress, Grid, Stack, Tooltip, Typography} from "@mui/material";
import {Title} from "../../NeonComponents/Texts";
import {NeonCard, NeonCardContent, NeonCardHeader} from "../../NeonComponents/NeonCards";
import {scrollable} from "../../theme";
import Box from "@mui/material/Box";
import formatters from "../../NeonComponents/Formatters";
import TeamIcon from "../../components/Icons/TeamIcon";
import {CompetitiveDraftSlots, CompetitiveGames, League, Team} from "../../reckon-api";
import {NeonGlassItem} from "../../NeonComponents/NeonGlassItem";
import {StatsBlock} from "../../NeonComponents/NeonStatsBlock";
import {getMapAsset} from "../../valorant-api/mapAssets";
import {NeonDialog, NeonDialogActions, NeonDialogContent, NeonDialogTitle} from "../../NeonComponents/NeonDialog";
import {NeonButton} from "../../NeonComponents/Buttons";
import TableDisplayGrid, {rowStyle} from "../../components/datagrid/TableDisplayGrid";
import {ColumnTypes} from "../../components/datagrid/styled_datagrid";
import {NeonTab, NeonTabs} from "../../NeonComponents/NeonTabs";

type DraftTab = "recent" | "stats";

interface DraftColumnConfig {
    title: string;
    kind: "pick" | "ban" | "decider";
    teamGroupby: string;
    teamFields: string[];
    requiresTeam: boolean;
    emptyHint: string;
    buildParams: (base: Record<string, any>) => Record<string, any>;
}

interface DraftDetail {
    title: string;
    request: () => Promise<any[]>;
    watermark: string;
}

interface SeriesDraft {
    series: number;
    date: string;
    league: string;
    team: string;
    opponent: string;
    slots: CompetitiveDraftSlots[];
}

interface GameResult {
    won: boolean;
    myScore: number;
    enemyScore: number;
}

const MAX_TEAM_ICONS = 8;
const TYPE_ORDER: Record<string, number> = {ban: 0, pick: 1, decider: 2};
const SLOT_STYLE: Record<string, {label: string; color: string; wash: string}> = {
    ban: {label: "Ban", color: "#ef5350", wash: "rgba(239, 83, 80, 0.08)"},
    pick: {label: "Pick", color: "#66bb6a", wash: "rgba(102, 187, 106, 0.07)"},
    decider: {label: "Decider", color: "#ffd54f", wash: "rgba(255, 213, 79, 0.07)"},
};

function sanitizeParams(paramObject: any): Record<string, any> {
    const {watermark, team, metrics, ...rest} = paramObject || {};
    return rest;
}

function toListParams(params: Record<string, any>, mapId: string) {
    return {
        additional_filters: params.additional_filters,
        draft_action_id: params.draft_action_id,
        league: params.league,
        map: mapId,
        ordering: ["-date"],
        phase: params.phase,
        series: params.series,
        split: params.split,
        team1: params.team1,
        team2: params.team2,
        teams: params.teams,
        type: params.type,
    };
}

function parseTeams(value: unknown): string[] {
    if (value && typeof value === "object" && !Array.isArray(value)) {
        const nested = (value as any).id ?? (value as any).name ?? (value as any).pk;
        return nested ? parseTeams(nested) : [];
    }
    if (Array.isArray(value)) {
        return [...new Set(value.flatMap((item) => parseTeams(item)))];
    }
    if (typeof value !== "string" || !value.trim()) {
        return [];
    }
    return [...new Set(value.split(/[,|;]/).map((t) => t.trim()).filter(Boolean))];
}

function uniqueGames(rows: CompetitiveGames[]): CompetitiveGames[] {
    const byId = new Map<string, CompetitiveGames>();
    rows.forEach((row) => {
        if (row?.id) {
            byId.set(row.id, row);
        }
    });
    return [...byId.values()];
}

function uniqueSlots(rows: CompetitiveDraftSlots[]): CompetitiveDraftSlots[] {
    const byId = new Map<number, CompetitiveDraftSlots>();
    rows.forEach((row) => {
        if (row?.id != null) {
            byId.set(row.id, row);
        }
    });
    return [...byId.values()];
}

function gameKey(seriesId: number | string | undefined, map?: string | null) {
    return `${seriesId ?? ""}::${(map || "").toLowerCase()}`;
}

function gameResultForTeam(game: CompetitiveGames | undefined, teamId: string): GameResult | undefined {
    if (!game || !teamId) return undefined;
    const isTeam1 = game.team1 === teamId;
    const isTeam2 = game.team2 === teamId;
    if (!isTeam1 && !isTeam2) return undefined;
    const myScore = isTeam1 ? game.score_team1 : game.score_team2;
    const enemyScore = isTeam1 ? game.score_team2 : game.score_team1;
    return {
        won: game.winner === teamId,
        myScore: myScore ?? 0,
        enemyScore: enemyScore ?? 0,
    };
}

function indexGamesBySeriesMap(games: CompetitiveGames[]) {
    const indexed = new Map<string, CompetitiveGames>();
    games.forEach((game) => {
        indexed.set(gameKey(game.series_id, game.map), game);
    });
    return indexed;
}

function recordForMap(games: CompetitiveGames[], mapId: string, teamId: string) {
    const mapGames = games.filter((game) => (game.map || "").toLowerCase() === mapId.toLowerCase());
    const wins = mapGames.filter((game) => game.winner === teamId).length;
    return {wins, losses: mapGames.length - wins, games: mapGames.length};
}

function sortSlots(slots: CompetitiveDraftSlots[]): CompetitiveDraftSlots[] {
    return [...slots].sort((a, b) => {
        const actionA = String(a.draft_action_id ?? "");
        const actionB = String(b.draft_action_id ?? "");
        if (actionA !== actionB) {
            return actionA.localeCompare(actionB, undefined, {numeric: true});
        }
        return (TYPE_ORDER[a.type] ?? 9) - (TYPE_ORDER[b.type] ?? 9);
    });
}

function groupSlotsBySeries(slots: CompetitiveDraftSlots[], selectedTeamId: string, vsTeamId?: string): SeriesDraft[] {
    const grouped = new Map<number, CompetitiveDraftSlots[]>();
    slots.forEach((slot) => {
        const key = slot.series;
        if (!grouped.has(key)) {
            grouped.set(key, []);
        }
        grouped.get(key)!.push(slot);
    });

    return [...grouped.entries()]
        .map(([series, seriesSlots]) => {
            const teams = new Set<string>();
            seriesSlots.forEach((slot) => {
                if (slot.team1) teams.add(slot.team1);
                if (slot.team2) teams.add(slot.team2);
            });
            teams.delete(selectedTeamId);
            const opponent = [...teams][0] ?? seriesSlots[0]?.team2 ?? "";
            const latest = seriesSlots.reduce(
                (max, slot) => (dayjs(slot.date).isAfter(max) ? slot.date : max),
                seriesSlots[0]?.date
            );
            return {
                series,
                date: latest,
                league: seriesSlots[0]?.league ?? "",
                team: selectedTeamId,
                opponent,
                slots: sortSlots(seriesSlots),
            };
        })
        .filter((series) => !vsTeamId || series.opponent === vsTeamId)
        .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
}

function teamIdFromRow(row: any, fields: string[]): string[] {
    const ids: string[] = [];
    fields.forEach((field) => {
        const raw = row?.[field] ?? row?.[`${field}_id`] ?? row?.[field.replace(/_id$/, "")];
        parseTeams(raw).forEach((id) => {
            if (id && !ids.includes(id)) {
                ids.push(id);
            }
        });
    });
    return ids;
}

function collapseRowsByMap(rows: any[], teamFields: string[], excludeTeamId?: string) {
    const byMap = new Map<string, {map: string; series__cnt: number; teams: string[]}>();
    rows.forEach((row) => {
        const map = row?.map;
        if (!map) return;
        const current = byMap.get(map) ?? {map, series__cnt: 0, teams: [] as string[]};
        current.series__cnt += Number(row.series__cnt || 0);
        teamIdFromRow(row, teamFields).forEach((id) => {
            if (id !== excludeTeamId && !current.teams.includes(id)) {
                current.teams.push(id);
            }
        });
        byMap.set(map, current);
    });
    return [...byMap.values()].sort((a, b) => b.series__cnt - a.series__cnt);
}

function draftColumns(): DraftColumnConfig[] {
    return [
        {
            title: "Picks",
            kind: "pick",
            teamGroupby: "team2",
            teamFields: ["team2", "team2_id"],
            requiresTeam: false,
            emptyHint: "No picks in this range",
            buildParams: (base) => ({...base, type: "pick"}),
        },
        {
            title: "Picks VS",
            kind: "pick",
            teamGroupby: "team1",
            teamFields: ["team1", "team1_id"],
            requiresTeam: true,
            emptyHint: "Select a team to see opponent picks",
            buildParams: (base) => ({
                ...base,
                type: "pick",
                team1: base.team2,
                team2: base.team1,
            }),
        },
        {
            title: "Bans",
            kind: "ban",
            teamGroupby: "team2",
            teamFields: ["team2", "team2_id"],
            requiresTeam: false,
            emptyHint: "No bans in this range",
            buildParams: (base) => ({...base, type: "ban"}),
        },
        {
            title: "Bans VS",
            kind: "ban",
            teamGroupby: "team1",
            teamFields: ["team1", "team1_id"],
            requiresTeam: true,
            emptyHint: "Select a team to see opponent bans",
            buildParams: (base) => ({
                ...base,
                type: "ban",
                team1: base.team2,
                team2: base.team1,
            }),
        },
        {
            title: "Deciders",
            kind: "decider",
            teamGroupby: "team1,team2",
            teamFields: ["team1", "team2", "team1_id", "team2_id"],
            requiresTeam: true,
            emptyHint: "Select a team to see leftover maps",
            buildParams: (base) => ({
                ...base,
                type: "decider",
                team1: undefined,
                teams: base.team1,
            }),
        },
    ];
}

export default function CompetitiveTeamDrafts() {
    const ctx = useReferentialContext();
    const [team, setTeam] = useState<Team | null>(ctx.selectedTeam);
    const [vs, setVs] = useState<Team | null>(null);
    const [leagues, setLeagues] = useState<League[]>(ctx.leaguesReferential);
    const [from, setFrom] = useState(dayjs(`${dayjs().year()}-01-01`));
    const [until, setUntil] = useState(dayjs());
    const [paramObject, setParamObject] = useState<any>({});
    const [tab, setTab] = useState<DraftTab>("recent");
    const [games, setGames] = useState<CompetitiveGames[]>([]);
    const leaguesInitialized = React.useRef(ctx.leaguesReferential.length > 0);

    useEffect(() => {
        setTeam(ctx.selectedTeam);
    }, [ctx.selectedTeam]);

    useEffect(() => {
        if (!leaguesInitialized.current && ctx.leaguesReferential.length > 0) {
            setLeagues(ctx.leaguesReferential);
            leaguesInitialized.current = true;
        }
    }, [ctx.leaguesReferential]);

    useEffect(() => {
        const teamId = paramObject.team1 as string | undefined;
        const vsId = paramObject.team2 as string | undefined;
        if (!paramObject.watermark || !teamId) {
            setGames([]);
            return;
        }

        let cancelled = false;
        const filters = {
            additional_filters: sanitizeParams(paramObject).additional_filters,
            ordering: ["-date"],
        };

        Promise.all([
            ctx.reckonApi.competitiveGames.list({...filters, team1: teamId}),
            ctx.reckonApi.competitiveGames.list({...filters, team2: teamId}),
        ])
            .then(([asTeam1, asTeam2]) => {
                if (cancelled) return;
                let merged = uniqueGames([...(asTeam1 || []), ...(asTeam2 || [])]);
                if (vsId) {
                    merged = merged.filter((game) => game.team1 === vsId || game.team2 === vsId);
                }
                setGames(merged);
            })
            .catch(() => {
                if (!cancelled) {
                    setGames([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [paramObject.watermark, paramObject.team1, paramObject.team2]);

    return (
        <WindowContainer spacing={1} sx={{height: "100%", minHeight: 0, overflow: "hidden"}}>
            <FilterSet
                paramObject={paramObject}
                setParamObject={setParamObject}
                target={targets.competitive}
                team={team}
                setTeam={setTeam}
                vs={vs}
                setVs={setVs}
                leagues={leagues}
                setLeagues={setLeagues}
                date__gte={from}
                setDateGte={setFrom}
                date__lt={until}
                setDateLt={setUntil}
                updateContext={true}
                updateLeaguesContext={false}
            />
            <NeonTabs
                value={tab}
                onChange={(_, value: DraftTab) => setTab(value)}
                sx={{flexShrink: 0, minHeight: "42px"}}
            >
                <NeonTab value="recent" label="Recent Drafts" sx={{minHeight: "42px"}}/>
                <NeonTab value="stats" label="Stats" sx={{minHeight: "42px"}}/>
            </NeonTabs>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: tab === "recent" ? "flex" : "none",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                <RecentDraftsTab paramObject={paramObject} games={games}/>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: tab === "stats" ? "flex" : "none",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                <DraftStatsTab paramObject={paramObject} games={games}/>
            </Box>
        </WindowContainer>
    );
}

function RecentDraftsTab(props: {paramObject: any; games: CompetitiveGames[]}) {
    const ctx = useReferentialContext();
    const [series, setSeries] = useState<SeriesDraft[]>([]);
    const [loading, setLoading] = useState(false);
    const selectedTeamId = props.paramObject.team1 as string | undefined;
    const vsTeamId = props.paramObject.team2 as string | undefined;
    const canFetch = Boolean(props.paramObject.watermark) && Boolean(selectedTeamId);
    const gamesBySeriesMap = useMemo(() => indexGamesBySeriesMap(props.games), [props.games]);

    useEffect(() => {
        if (!canFetch || !selectedTeamId) {
            setSeries([]);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        const filters = {
            additional_filters: sanitizeParams(props.paramObject).additional_filters,
            ordering: ["-date", "draft_action_id"],
        };

        Promise.all([
            ctx.reckonApi.competitiveDraftSlots.list({...filters, team1: selectedTeamId}),
            ctx.reckonApi.competitiveDraftSlots.list({...filters, team2: selectedTeamId}),
        ])
            .then(([asActor, asOpponent]) => {
                if (cancelled) return;
                const slots = uniqueSlots([...(asActor || []), ...(asOpponent || [])]);
                setSeries(groupSlotsBySeries(slots, selectedTeamId, vsTeamId));
            })
            .catch(() => {
                if (!cancelled) {
                    setSeries([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [canFetch, props.paramObject.watermark, selectedTeamId, vsTeamId]);

    if (!selectedTeamId) {
        return (
            <Stack sx={{width: "100%", height: "100%", alignItems: "center", justifyContent: "center"}}>
                <Typography variant="body2" sx={{color: "rgba(255,255,255,0.55)"}}>
                    Select a team to see recent drafts
                </Typography>
            </Stack>
        );
    }

    if (loading) {
        return (
            <Stack sx={{width: "100%", height: "100%", alignItems: "center", justifyContent: "center"}}>
                <CircularProgress size={28}/>
            </Stack>
        );
    }

    if (series.length === 0) {
        return (
            <Stack sx={{width: "100%", height: "100%", alignItems: "center", justifyContent: "center"}}>
                <Typography variant="body2" sx={{color: "rgba(255,255,255,0.55)"}}>
                    No drafts in this range
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack
            spacing={1.25}
            sx={{
                width: "100%",
                height: "100%",
                minHeight: 0,
                pr: 0.5,
                ...scrollable,
            }}
        >
            {series.map((draft) => (
                <SeriesDraftCard
                    key={draft.series}
                    draft={draft}
                    selectedTeamId={selectedTeamId}
                    gamesBySeriesMap={gamesBySeriesMap}
                />
            ))}
        </Stack>
    );
}

function SeriesDraftCard(props: {
    draft: SeriesDraft;
    selectedTeamId: string;
    gamesBySeriesMap: Map<string, CompetitiveGames>;
}) {
    const playedResults = props.draft.slots
        .filter((slot) => slot.type !== "ban")
        .map((slot) => gameResultForTeam(props.gamesBySeriesMap.get(gameKey(props.draft.series, slot.map)), props.selectedTeamId))
        .filter((result): result is GameResult => Boolean(result));
    const seriesWins = playedResults.filter((result) => result.won).length;
    const seriesLosses = playedResults.length - seriesWins;

    return (
        <NeonGlassItem
            sx={{
                width: "100%",
                p: 1.5,
                flexShrink: 0,
            }}
        >
            <Stack direction="row" spacing={2} sx={{alignItems: "center", mb: 1.25, minWidth: 0}}>
                <Typography sx={{fontWeight: 700, minWidth: 72}}>
                    {props.draft.date ? dayjs(props.draft.date).format("MMM DD") : ""}
                </Typography>
                <Typography variant="body2" sx={{color: "rgba(255,255,255,0.7)", minWidth: 90}}>
                    {props.draft.league}
                </Typography>
                <Stack direction="row" spacing={1} sx={{alignItems: "center"}}>
                    <TeamIcon team_name={props.draft.team} sx={{width: 28, height: 28}}/>
                    <Typography variant="caption" sx={{color: "rgba(255,255,255,0.55)"}}>vs</Typography>
                    {props.draft.opponent ? (
                        <TeamIcon team_name={props.draft.opponent} sx={{width: 28, height: 28}}/>
                    ) : null}
                </Stack>
                {playedResults.length > 0 ? (
                    <MapScoreLine wins={seriesWins} losses={seriesLosses} compact={false}/>
                ) : null}
            </Stack>
            <Stack
                direction="row"
                spacing={1}
                sx={{
                    alignItems: "stretch",
                    overflowX: "auto",
                    pb: 0.5,
                    ...scrollable,
                }}
            >
                {props.draft.slots.map((slot) => (
                    <DraftSlotTile
                        key={slot.id}
                        slot={slot}
                        owned={slot.type === "decider" ? false : slot.team1 === props.selectedTeamId}
                        result={
                            slot.type === "ban"
                                ? undefined
                                : gameResultForTeam(props.gamesBySeriesMap.get(gameKey(props.draft.series, slot.map)), props.selectedTeamId)
                        }
                    />
                ))}
            </Stack>
        </NeonGlassItem>
    );
}

function MapScoreLine(props: {wins: number; losses: number; compact?: boolean}) {
    const wonSeries = props.wins > props.losses;
    return (
        <Typography
            sx={{
                fontWeight: 700,
                fontSize: props.compact ? "0.75rem" : "0.9rem",
                letterSpacing: "0.04em",
                color: wonSeries ? "#81c784" : "#e57373",
            }}
        >
            {props.wins}-{props.losses}
        </Typography>
    );
}

function GameScore(props: {result: GameResult}) {
    return (
        <Typography
            sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                letterSpacing: "0.03em",
                color: props.result.won ? "#81c784" : "#e57373",
                textShadow: "0 1px 6px rgba(0,0,0,0.9)",
            }}
        >
            {props.result.myScore}-{props.result.enemyScore}
        </Typography>
    );
}

function DraftSlotTile(props: {slot: CompetitiveDraftSlots; owned: boolean; result?: GameResult}) {
    const style = SLOT_STYLE[props.slot.type] ?? {label: capitalize(props.slot.type || "slot"), color: "#ffffff", wash: "rgba(255,255,255,0.08)"};
    const banner = props.slot.map
        ? getMapAsset(props.slot.map, "listViewIcon").replace("/upload/", "/upload/c_fill,h_90,w_180/")
        : undefined;
    const isBan = props.slot.type === "ban";

    return (
        <Tooltip
            title={`${style.label} · ${props.slot.map ? capitalize(props.slot.map) : "Unknown"}${props.result ? ` · ${props.result.myScore}-${props.result.enemyScore}` : ""}`}
            followCursor
        >
            <Box
                sx={{
                    position: "relative",
                    width: 148,
                    minWidth: 148,
                    height: props.result ? 102 : 92,
                    borderRadius: "6px",
                    overflow: "hidden",
                    backgroundColor: style.wash,
                    border: `1px solid ${props.result ? (props.result.won ? "#81c78433" : "#e5737333") : `${style.color}2e`}`,
                    boxShadow: props.owned ? `0 0 8px ${style.color}22` : "none",
                    opacity: props.owned || props.slot.type === "decider" ? 1 : 0.78,
                }}
            >
                {banner ? (
                    <Box
                        component="img"
                        src={banner}
                        alt={props.slot.map}
                        sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            filter: isBan ? "grayscale(0.85) brightness(0.55)" : "brightness(0.72)",
                        }}
                    />
                ) : null}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background: `linear-gradient(180deg, rgba(0,0,0,0.22) 0%, ${style.color}1f 100%)`,
                    }}
                />
                <Stack
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        height: "100%",
                        p: 0.75,
                        justifyContent: "space-between",
                    }}
                >
                    <Stack direction="row" sx={{alignItems: "center", justifyContent: "space-between"}}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 800,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                color: style.color,
                                textShadow: "0 0 6px rgba(0,0,0,0.8)",
                            }}
                        >
                            {style.label}
                        </Typography>
                        {props.slot.team1 && props.slot.type !== "decider" ? (
                            <TeamIcon team_name={props.slot.team1} sx={{width: 20, height: 20}}/>
                        ) : null}
                    </Stack>
                    <Stack spacing={0.15}>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "0.9rem",
                            textShadow: "0 1px 6px rgba(0,0,0,0.9)",
                        }}
                    >
                        {props.slot.map ? capitalize(props.slot.map) : "Unknown"}
                    </Typography>
                    {props.result ? <GameScore result={props.result}/> : null}
                    </Stack>
                </Stack>
            </Box>
        </Tooltip>
    );
}

function DraftStatsTab(props: {paramObject: any; games: CompetitiveGames[]}) {
    const ctx = useReferentialContext();
    const columns = useMemo(() => draftColumns(), []);
    const [selectedMap, setSelectedMap] = useState<string | undefined>();
    const [detail, setDetail] = useState<DraftDetail | null>(null);

    const openMapDetails = (config: DraftColumnConfig, mapId: string) => {
        const query = toListParams(config.buildParams(sanitizeParams(props.paramObject)), mapId);
        setSelectedMap(mapId);
        setDetail({
            title: `${config.title} · ${capitalize(mapId)}`,
            watermark: `${config.title}-${mapId}-${props.paramObject.watermark}`,
            request: () => ctx.reckonApi.competitiveDraftSlots.list(query).then((rows) => rows || []),
        });
    };

    return (
        <Box sx={{width: "100%", height: "100%", minHeight: 0, display: "flex", flexDirection: "column"}}>
            <Grid container spacing={1} columns={15} sx={{flex: 1, minHeight: 0}}>
                {columns.map((config) => (
                    <Grid key={config.title} size={3} sx={{height: "100%", minHeight: 0}}>
                        <DraftColumn
                            config={config}
                            paramObject={props.paramObject}
                            games={props.games}
                            selectedMap={selectedMap}
                            onSelectMap={(mapId) => openMapDetails(config, mapId)}
                        />
                    </Grid>
                ))}
            </Grid>
            <NeonDialog
                open={detail !== null}
                onClose={() => setDetail(null)}
                maxWidth="lg"
                fullWidth
                PaperProps={{sx: {height: "70vh"}}}
            >
                <NeonDialogTitle>{detail?.title ?? ""}</NeonDialogTitle>
                <NeonDialogContent sx={{p: 0, overflow: "hidden"}}>
                    {detail && (
                        <TableDisplayGrid
                            request={detail.request}
                            display_all={false}
                            custom_columns={[
                                ColumnTypes.date("date", "Date"),
                                ColumnTypes.league("league", "League"),
                                {
                                    field: "team1",
                                    headerName: "Team",
                                    minWidth: 50,
                                    maxWidth: 60,
                                    align: "center",
                                    headerAlign: "center",
                                    renderCell: (params: any) =>
                                        params.value ? <TeamIcon team_name={params.value} sx={{width: 28, height: 28}}/> : "",
                                },
                                {
                                    field: "team2",
                                    headerName: "Vs",
                                    minWidth: 50,
                                    maxWidth: 60,
                                    align: "center",
                                    headerAlign: "center",
                                    renderCell: (params: any) =>
                                        params.value ? <TeamIcon team_name={params.value} sx={{width: 28, height: 28}}/> : "",
                                },
                                {
                                    field: "map",
                                    headerName: "Map",
                                    minWidth: 90,
                                    flex: 1,
                                    align: "center",
                                    headerAlign: "center",
                                    renderCell: (params: any) => (params.value ? capitalize(params.value) : ""),
                                },
                                ColumnTypes.string("type", "Type"),
                                ColumnTypes.short_int("series", "Series"),
                            ]}
                            rowStyle={rowStyle.none}
                            sortBy="date"
                            paramObject={{watermark: detail.watermark}}
                        />
                    )}
                </NeonDialogContent>
                <NeonDialogActions>
                    <NeonButton variant="outlined" onClick={() => setDetail(null)}>
                        Close
                    </NeonButton>
                </NeonDialogActions>
            </NeonDialog>
        </Box>
    );
}

function DraftColumn(props: {
    config: DraftColumnConfig;
    paramObject: any;
    games: CompetitiveGames[];
    selectedMap?: string;
    onSelectMap: (mapId: string) => void;
}) {
    const ctx = useReferentialContext();
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const hasTeam = Boolean(props.paramObject.team1);
    const canFetch = Boolean(props.paramObject.watermark) && (!props.config.requiresTeam || hasTeam);
    const rowsWithResults = useMemo(() => {
        if (props.config.kind === "ban" || !props.paramObject.team1) {
            return rows;
        }
        return rows.map((row) => ({
            ...row,
            ...recordForMap(props.games, row.map, props.paramObject.team1),
        }));
    }, [rows, props.games, props.config.kind, props.paramObject.team1]);

    useEffect(() => {
        if (!canFetch) {
            setRows([]);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        const query = {
            ...props.config.buildParams(sanitizeParams(props.paramObject)),
            groupby: `map,${props.config.teamGroupby}` as any,
            aggregates: ["series__cnt"],
            ordering: ["-series__cnt"],
        };

        ctx.reckonApi.competitiveDraftSlots
            .aggregate(query)
            .then((result) => {
                if (!cancelled) {
                    const rows = Array.isArray(result) ? result : [];
                    setRows(collapseRowsByMap(rows, props.config.teamFields, props.paramObject.team1));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setRows([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [canFetch, props.paramObject.watermark, props.config]);

    return (
        <NeonCard sx={{width: "100%", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden"}}>
            <NeonCardHeader title={props.config.title} sx={{flexShrink: 0}}/>
            <NeonCardContent
                sx={{
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    justifyContent: "flex-start",
                    overflow: "hidden",
                    padding: "8px 12px 12px",
                }}
            >
                <Stack
                    direction="column"
                    spacing={1}
                    sx={{
                        alignItems: "center",
                        justifyContent: "flex-start",
                        display: "flex",
                        width: "100%",
                        height: "100%",
                        minHeight: 0,
                        ...scrollable,
                        "&::-webkit-scrollbar:horizontal": {
                            display: "none",
                        },
                    }}
                >
                    {loading ? (
                        <Stack sx={{width: "100%", height: "100%", alignItems: "center", justifyContent: "center"}}>
                            <CircularProgress size={28}/>
                        </Stack>
                    ) : rowsWithResults.length === 0 ? (
                        <Typography
                            variant="body2"
                            sx={{color: "rgba(255,255,255,0.55)", textAlign: "center", pt: 4, px: 1}}
                        >
                            {props.config.requiresTeam && !hasTeam ? props.config.emptyHint : "No draft data for these filters"}
                        </Typography>
                    ) : (
                        rowsWithResults.map((row) => (
                            <DraftMapRow
                                key={`${props.config.title}-${row.map}`}
                                stats={row}
                                kind={props.config.kind}
                                selected={row.map === props.selectedMap}
                                onClick={() => row.map && props.onSelectMap(row.map)}
                            />
                        ))
                    )}
                </Stack>
            </NeonCardContent>
        </NeonCard>
    );
}

function DraftMapRow(props: {
    stats: {map?: string; series__cnt?: number; teams?: string[]; wins?: number; losses?: number};
    kind: "pick" | "ban" | "decider";
    selected: boolean;
    onClick: () => void;
}) {
    const mapId = props.stats?.map;
    const teams = (props.stats?.teams ?? []).filter(Boolean);
    const visibleTeams = teams.slice(0, MAX_TEAM_ICONS);
    const extraTeams = teams.length - visibleTeams.length;
    const style = SLOT_STYLE[props.kind];
    const banner = mapId
        ? getMapAsset(mapId, "listViewIcon").replace("/upload/", "/upload/c_fill,h_120,w_360/")
        : undefined;

    return (
        <Box sx={{width: "100%", display: "flex", alignItems: "center", justifyContent: "center"}}>
            <NeonGlassItem
                isClickable
                onClick={props.onClick}
                sx={{
                    width: "100%",
                    minHeight: 96,
                    p: 1.25,
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: style.wash,
                    border: `1px solid ${style.color}24`,
                    "&::before": props.selected
                        ? {
                              content: "''",
                              position: "absolute",
                              top: "15%",
                              bottom: "15%",
                              left: "0px",
                              width: "2px",
                              height: "70%",
                              zIndex: 2,
                              background: "#ffffff",
                              boxShadow: "0px 0px 5px #fff, 0px 0px 20px #fff",
                          }
                        : {},
                }}
            >
                {banner ? (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundImage: `url(${banner})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            opacity: 0.28,
                            pointerEvents: "none",
                        }}
                    />
                ) : null}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background: `linear-gradient(90deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.22) 55%, ${style.color}14 100%)`,
                        pointerEvents: "none",
                    }}
                />
                <Grid container columns={4} spacing={0.75} sx={{position: "relative", zIndex: 1}}>
                    <Grid size={3} sx={{display: "flex", alignItems: "center", gap: 1, pl: 0.5}}>
                        <Title sx={{fontSize: "1.05rem", textAlign: "left"}}>
                            {mapId ? capitalize(mapId) : "Unknown"}
                        </Title>
                        {props.kind !== "ban" && (props.stats.wins || props.stats.losses) ? (
                            <MapScoreLine wins={props.stats.wins ?? 0} losses={props.stats.losses ?? 0} compact/>
                        ) : null}
                    </Grid>
                    <Grid size={1} sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                        <StatsBlock
                            label="Series"
                            value={props.stats.series__cnt ?? 0}
                            metricCode="series_cnt"
                            formatter={formatters.integer}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Stack
                            direction="row"
                            spacing={0.5}
                            useFlexGap
                            sx={{
                                alignItems: "center",
                                justifyContent: "flex-start",
                                flexWrap: "wrap",
                                width: "100%",
                                minHeight: 28,
                                pl: 0.5,
                            }}
                        >
                            {visibleTeams.map((teamName) => (
                                <TeamIcon key={teamName} team_name={teamName} sx={{width: 22, height: 22}}/>
                            ))}
                            {extraTeams > 0 ? (
                                <Tooltip title={teams.slice(MAX_TEAM_ICONS).join(", ")} followCursor>
                                    <Typography variant="caption" sx={{color: "rgba(255,255,255,0.75)", pl: 0.25}}>
                                        +{extraTeams}
                                    </Typography>
                                </Tooltip>
                            ) : null}
                            {teams.length === 0 ? (
                                <Typography variant="caption" sx={{color: "rgba(255,255,255,0.45)"}}>
                                    No teams listed
                                </Typography>
                            ) : null}
                        </Stack>
                    </Grid>
                </Grid>
            </NeonGlassItem>
        </Box>
    );
}
